import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';

// ============================================================
// Types
// ============================================================

export type TaskAssignment = Tables<'task_assignments'>;
export type TaskComment = Tables<'task_comments'>;
export type TaskAttachment = Tables<'task_attachments'>;

export type CollaboratorSummary = Pick<
  Tables<'collaborators'>,
  'id' | 'full_name' | 'email' | 'avatar_url' | 'role'
>;

export interface TaskCommentWithAuthor extends TaskComment {
  author: CollaboratorSummary | null;
}

// ============================================================
// Assignations
// ============================================================

export async function getTaskAssignees(taskId: string): Promise<CollaboratorSummary[]> {
  const { data, error } = await supabase
    .from('task_assignments')
    .select(
      'assignee:collaborators!task_assignments_assignee_id_fkey(id, full_name, email, avatar_url, role)',
    )
    .eq('task_id', taskId);
  if (error) throw new Error(`Failed to fetch assignees: ${error.message}`);
  return (data ?? [])
    .map((r) => (r as unknown as { assignee: CollaboratorSummary | null }).assignee)
    .filter((c): c is CollaboratorSummary => !!c);
}

export async function getAssignmentsByTaskIds(
  taskIds: string[],
): Promise<Map<string, CollaboratorSummary[]>> {
  if (taskIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from('task_assignments')
    .select(
      'task_id, assignee:collaborators!task_assignments_assignee_id_fkey(id, full_name, email, avatar_url, role)',
    )
    .in('task_id', taskIds);
  if (error) throw new Error(`Failed to fetch assignments: ${error.message}`);
  const map = new Map<string, CollaboratorSummary[]>();
  for (const row of data ?? []) {
    const r = row as unknown as { task_id: string; assignee: CollaboratorSummary | null };
    if (!r.assignee) continue;
    const arr = map.get(r.task_id) ?? [];
    arr.push(r.assignee);
    map.set(r.task_id, arr);
  }
  return map;
}

export async function assignTask(taskId: string, assigneeId: string): Promise<void> {
  const { error } = await supabase.rpc('assign_task', {
    p_task_id: taskId,
    p_assignee_id: assigneeId,
  });
  if (error) throw new Error(`Failed to assign task: ${error.message}`);
}

export async function unassignTask(taskId: string, assigneeId: string): Promise<void> {
  const { error } = await supabase.rpc('unassign_task', {
    p_task_id: taskId,
    p_assignee_id: assigneeId,
  });
  if (error) throw new Error(`Failed to unassign task: ${error.message}`);
}

// ============================================================
// Commentaires
// ============================================================

export async function getTaskComments(taskId: string): Promise<TaskCommentWithAuthor[]> {
  const { data, error } = await supabase
    .from('task_comments')
    .select(
      '*, author:collaborators!task_comments_author_id_fkey(id, full_name, email, avatar_url, role)',
    )
    .eq('task_id', taskId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(`Failed to fetch comments: ${error.message}`);
  return (data ?? []) as unknown as TaskCommentWithAuthor[];
}

export async function getCommentCountsByTaskIds(
  taskIds: string[],
): Promise<Map<string, number>> {
  if (taskIds.length === 0) return new Map();
  // Comme Supabase JS n'a pas de groupBy direct, on fetch tous les ids puis on compte côté client.
  const { data, error } = await supabase
    .from('task_comments')
    .select('task_id')
    .in('task_id', taskIds)
    .eq('is_deleted', false);
  if (error) throw new Error(`Failed to fetch comment counts: ${error.message}`);
  const map = new Map<string, number>();
  for (const r of data ?? []) {
    map.set(r.task_id, (map.get(r.task_id) ?? 0) + 1);
  }
  return map;
}

export async function addTaskComment(
  taskId: string,
  content: string,
): Promise<TaskComment> {
  const { data, error } = await supabase.rpc('add_task_comment', {
    p_task_id: taskId,
    p_content: content,
  });
  if (error) throw new Error(`Failed to add comment: ${error.message}`);
  if (!data) throw new Error('add_task_comment returned no data');
  return data as TaskComment;
}

export async function deleteTaskComment(commentId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_task_comment', {
    p_comment_id: commentId,
  });
  if (error) throw new Error(`Failed to delete comment: ${error.message}`);
}

// ============================================================
// Pièces jointes
// ============================================================

export async function getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
  const { data, error } = await supabase
    .from('task_attachments')
    .select('*')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`Failed to fetch attachments: ${error.message}`);
  return data ?? [];
}

export async function getAttachmentCountsByTaskIds(
  taskIds: string[],
): Promise<Map<string, number>> {
  if (taskIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from('task_attachments')
    .select('task_id')
    .in('task_id', taskIds);
  if (error) throw new Error(`Failed to fetch attachment counts: ${error.message}`);
  const map = new Map<string, number>();
  for (const r of data ?? []) {
    map.set(r.task_id, (map.get(r.task_id) ?? 0) + 1);
  }
  return map;
}

export async function uploadTaskAttachment(
  taskId: string,
  file: File,
): Promise<TaskAttachment> {
  // Path : <taskId>/<timestamp>-<sanitized-filename>
  const safeName = file.name.replace(/[^\w.\-]+/g, '_');
  const path = `${taskId}/${Date.now()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from('task-attachments')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });
  if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

  // Enregistre la metadata via RPC (l'RPC vérifie auth + bypass RLS)
  const { data, error } = await supabase.rpc('add_task_attachment', {
    p_task_id: taskId,
    p_storage_path: path,
    p_filename: file.name,
    p_mime_type: file.type || 'application/octet-stream',
    p_size_bytes: file.size,
  });
  if (error) {
    // Best-effort cleanup du fichier orphelin
    await supabase.storage.from('task-attachments').remove([path]);
    throw new Error(`Failed to register attachment: ${error.message}`);
  }
  if (!data) throw new Error('add_task_attachment returned no data');
  return data as TaskAttachment;
}

export async function deleteTaskAttachment(attachmentId: string): Promise<void> {
  // RPC retourne le path à supprimer du Storage
  const { data: path, error } = await supabase.rpc('delete_task_attachment', {
    p_attachment_id: attachmentId,
  });
  if (error) throw new Error(`Failed to delete attachment: ${error.message}`);
  if (path) {
    await supabase.storage.from('task-attachments').remove([path]);
  }
}

export async function getAttachmentSignedUrl(
  storagePath: string,
  expiresIn = 60,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('task-attachments')
    .createSignedUrl(storagePath, expiresIn);
  if (error || !data?.signedUrl) {
    throw new Error(`Failed to create signed URL: ${error?.message ?? 'no url'}`);
  }
  return data.signedUrl;
}

// ============================================================
// Liste collaborators (pour dropdown assign)
// ============================================================

export async function getActiveCollaborators(): Promise<CollaboratorSummary[]> {
  const { data, error } = await supabase
    .from('collaborators')
    .select('id, full_name, email, avatar_url, role')
    .eq('is_active', true)
    .order('full_name', { ascending: true });
  if (error) throw new Error(`Failed to fetch collaborators: ${error.message}`);
  return data ?? [];
}

/**
 * Bouton « Relancer » : notification dans l'application + mail aux personnes
 * assignées à la tâche (à défaut, l'avocat responsable du dossier).
 */
export async function nudgeTask(taskId: string): Promise<{ recipients: string[]; emailOk: boolean }> {
  const { data, error } = await supabase.functions.invoke('notify-task-nudge', {
    body: { task_id: taskId },
  });
  if (error) {
    // supabase-js range le corps de la réponse dans error.context.
    let message = 'Impossible d’envoyer la relance';
    try {
      const body = await (error as { context?: Response }).context?.json();
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      /* corps illisible : message générique */
    }
    throw new Error(message);
  }
  return {
    recipients: Array.isArray(data?.recipients) ? (data.recipients as string[]) : [],
    emailOk: data?.email_ok === true,
  };
}
